import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { loginWithEmail, logoutUser, onAuthChange, syncFcmToken } from '../firebase';
import type { User } from 'firebase/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  userType: 'tim' | 'diya' | null;
  login: (person: 'tim' | 'diya', password: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Map person to their Firebase email
const PERSON_TO_EMAIL: { [key in 'tim' | 'diya']: string } = {
  tim: 'tim@imissyou.app',  
  diya: 'diya@imissyou.app'
};

// Reverse mapping
const EMAIL_TO_PERSON: { [key: string]: 'tim' | 'diya' } = {
  'tim@imissyou.app': 'tim',
  'diya@imissyou.app': 'diya'
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userType, setUserType] = useState<'tim' | 'diya' | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser: User) => {
      setUser(firebaseUser);
      if (firebaseUser?.email) {
        setUserType(EMAIL_TO_PERSON[firebaseUser.email]);
        await syncFcmToken(EMAIL_TO_PERSON[firebaseUser.email]);
      } else {
        setUserType(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (person: 'tim' | 'diya', password: string) => {
    try {
      setError(null);
      const email = PERSON_TO_EMAIL[person];
      await loginWithEmail(email, password);
    } catch (err: any) {
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.message.includes('invalid-credential') || err.message.includes('wrong-password')) {
        errorMessage = 'Incorrect password. Try again! 💔';
      } else if (err.message.includes('user-not-found')) {
        errorMessage = 'Account not found. Please contact admin.';
      } else if (err.message.includes('too-many-requests')) {
        errorMessage = 'Too many attempts. Please wait a bit.';
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
      setUserType(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, userType, login, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};